import { Component, OnInit, Pipe, PipeTransform } from '@angular/core';
import { PopupService } from '../modal/popup.service';
import { ActivatedRoute } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../alert/alert.service';
import { finalize } from 'rxjs/internal/operators/finalize';
import { formatErrorList } from '../alert/format-error-list';
import { JhiEventManager } from '../core-utils';
import { SubscriptionsManager } from '../util/subscriptions-manager';
import { QueryService } from './service/query.service';
import { StudyReference } from './model/study-reference.model';

@Component({
    selector: 'jhi-step-specify-and-weight-the-environments-study-filter',
    templateUrl: './study-filter.component.html'
})
export class StudyFilterComponent implements OnInit {
    studyFilter: number[];
    selectedTraits: number[];
    environmentIds: number[];
    subscriptionManager = new SubscriptionsManager();
    traitStudies: StudyReference[];
    selectedItems: Map<number, StudyReference> = new Map<number, StudyReference>();
    lastClickIndex: any;
    studyFilterText: string = '';
    traitFilterPlaceHolder = '';
    isLoading: boolean;

    constructor(
        private queryService: QueryService,
        private translateService: TranslateService,
        private alertService: AlertService,
        private modal: NgbActiveModal,
        private modalService: NgbModal,
        private eventManager: JhiEventManager,
    ) {
        this.traitStudies = [];
    }

    ngOnInit(): void {
        this.isLoading = true;
        this.subscriptionManager.add = this.queryService.getTraitStudies(this.selectedTraits, this.environmentIds).pipe(finalize(() => {
            this.isLoading = false;
        })).subscribe((res: StudyReference[]) => this.onSuccess(res));

        this.traitFilterPlaceHolder = this.translateService.instant('multi-trait-query.panel.specify.and.weight.the.environments.environment.filter.study.place.holder');
    }

    trackId(index: number, item: any) {
        return item.id;
    }

    clearSelectedItems() {
        this.selectedItems.clear();
    }

    isPageSelected() {
        return this.selectedItems.size && this.traitStudies.every((entry: StudyReference) => Boolean(this.selectedItems.get(entry.id)));
    }

    onSelectPage() {
        if (this.isPageSelected()) {
            this.traitStudies.forEach((entry) => this.selectedItems.delete(entry.id));
        } else {
            this.traitStudies.forEach((entry) => this.selectedItems.set(entry.id, entry));
        }
    }

    toggleSelect($event, index, entry: StudyReference, checkbox = false) {
        if (!$event.ctrlKey && !checkbox) {
            this.clearSelectedItems();
        }
        let items;
        if ($event.shiftKey) {
            const max = Math.max(this.lastClickIndex, index) + 1,
                min = Math.min(this.lastClickIndex, index);
            items = this.traitStudies.slice(min, max);
        } else {
            items = [entry];
            this.lastClickIndex = index;
        }
        const isClickedItemSelected = this.selectedItems.get(entry.id);
        for (const item of items) {
            if (isClickedItemSelected) {
                this.selectedItems.delete(item.id);
            } else {
                this.selectedItems.set(item.id, item);
            }
        }
    }

    isSelected(entry: StudyReference) {
        return this.selectedItems.get(entry.id);
    }

    reset() {
        this.modal.close();
        this.studyFilter = [];
        const content = {
            'studyFilter': this.studyFilter
        };
        this.eventManager.broadcast({ name: 'environmentFilterViewChanged', content });
    }

    dismiss() {
        this.modal.dismiss();
    }

    apply() {
        this.modal.close();
        this.studyFilter = Array.from(this.selectedItems.values()).map(value => value.id);
        const content = {
            'studyFilter': this.studyFilter
        };
        this.eventManager.broadcast({ name: 'environmentFilterViewChanged', content });
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    private onSuccess(data: StudyReference[]) {
        if (this.studyFilter.length !== 0) {
            data
                .filter(study => this.studyFilter.includes(study.id)).forEach((entry) => this.selectedItems.set(entry.id, entry));
        }
        this.traitStudies = data;
    }

}


@Component({
    selector: 'jhi-specify-and-weight-environment-study-filter-popup',
    template: ''
})
export class StudyFilterPopupComponent implements OnInit {
    routeSub: any;
    studyFilter: number[];
    environmentIds: number[];
    selectedTraits: number[];

    constructor(
        private route: ActivatedRoute,
        private popupService: PopupService
    ) {
        const queryParams = this.route.snapshot.queryParams;
        this.selectedTraits = this.convertToArray(queryParams.selectedTraits);
        this.environmentIds = this.convertToArray(queryParams.environmentIds);
        this.studyFilter = this.convertToArray(queryParams.studyFilter);
    }

    ngOnDestroy(): void {
        if (this.routeSub) {
            this.routeSub.unsubscribe();
        }
    }

    ngOnInit(): void {
        this.routeSub = this.route.params.subscribe(() => {
            const modal = this.popupService.open(StudyFilterComponent as Component, {
                modalDialogClass: 'modal-lg',
                backdrop: 'static'
            });
            modal.then((modalRef) => {
                modalRef.componentInstance.selectedTraits = this.selectedTraits;
                modalRef.componentInstance.environmentIds = this.environmentIds;
                modalRef.componentInstance.studyFilter = this.studyFilter;
            });
        });
    }

    private convertToArray(param): number[] {
        if (!param) {
            return [];
        }

        const numbersArray = Array.isArray(param) ? param : [param];
        return  numbersArray.map(n => Number(n));
    }

}

@Pipe({
    name: 'studyFilter',
    standalone: true
})
export class StudyFilterPipe implements PipeTransform {
    transform(studies: StudyReference[], search: string): StudyReference[] {
        if (!search) {
            return studies;
        }

        const filter = search.toLowerCase();
        return studies.filter(s =>
            s.name.toLowerCase().includes(filter) ||
            s.description.toLowerCase().includes(filter)
        );
    }
}
